import type { FC } from 'react';
import { useMemo, useState, useEffect } from 'react';
import { getTimeDataList, getCeilingTime, getDateInfo, splitArrayInGroups } from '../Calendar/CalendarHelper';
import FAIcon, { IconStyle } from '../../Icons/Icons';
import { EVENT_KEY, Theme } from '../../types/enums';
import { TimeMeridiem } from '../types/enums';
import styles from './TimePicker.module.scss';
import type { TimePickerProps } from './../types/propTypes';

const MAX_COLUMNS = 4;
const MAX_TIME_INPUT_LENGTH = 2;
const MAX_MINS = 60;
const MAX_HOURS_IN_12_HOUR_FORMAT = 12;
const MAX_HOURS_IN_24_HOUR_FORMAT = 24;

export const getHoursIn12HourFormat = (hour: string): string => {
  if (Number(hour) === 0) {
    return MAX_HOURS_IN_12_HOUR_FORMAT.toString();
  }
  if (Number(hour) > 12) {
    return (Number(hour) - 12).toString();
  }
  return hour;
};

export const getHoursIn24HourFormat = (hour: string): string => {
  if (Number(hour) === 0) {
    return MAX_HOURS_IN_12_HOUR_FORMAT.toString();
  }
  return hour;
};

const TimePicker: FC<TimePickerProps> = ({
  title,
  is24HoursFormat = true,
  value,
  onTimeChange,
  disablePastTime = false,
  disableTime,
}) => {
  const [hours, setHours] = useState<string | undefined>(String());
  const [minutes, setMinutes] = useState<string | undefined>(String());
  const [meridiem, setMeridiem] = useState<string>(TimeMeridiem.AM);
  const timeList = useMemo(() => getTimeDataList(is24HoursFormat), [is24HoursFormat]);
  useEffect(() => {
    if (value) {
      let currentValue = value;
      if (!is24HoursFormat) {
        const [time, meridiem] = value.split(' ');
        currentValue = time as string;
        setMeridiem(meridiem === TimeMeridiem.AM ? TimeMeridiem.AM : TimeMeridiem.PM);
      }
      const [hour, minute] = currentValue.split(':');
      setHours(hour);
      setMinutes(minute);
    } else {
      const { hour: currentInitialHour, minute: currentInitialMinute } = getDateInfo(false);
      const initialFormattedTime = getCeilingTime(
        is24HoursFormat,
        `${currentInitialHour}:${currentInitialMinute}`,
      ) as string;
      const [initialHour, interimValue] = initialFormattedTime.split(':');
      const [initialMinute, initialMeridiem] = interimValue?.split(' ') as string[];
      setHours(initialHour);
      setMinutes(initialMinute);
      setMeridiem(initialMeridiem as string);
    }
  }, [value]);
  useEffect(() => {
    if (value) {
      let currentValue = getCeilingTime(is24HoursFormat, value) as string;
      if (!is24HoursFormat) {
        const [time, meridiem] = currentValue.split(' ');
        currentValue = time as string;
        setMeridiem(meridiem === TimeMeridiem.AM ? TimeMeridiem.AM : TimeMeridiem.PM);
      }
      const [hour, minute] = currentValue.split(':');
      setHours(hour);
      setMinutes(minute);
    }
  }, []);
  const isValidSelection = (splitArray: string[]) => splitArray[0] === hours && splitArray[1] === minutes;
  const isDisabledTimeStamp = (time: string, hour: string, currentHour: string) => {
    if (disableTime) {
      return disableTime(time);
    }
    if (disablePastTime) {
      return Number(hour) < Number(currentHour);
    }
    return false;
  };
  const handleTimeStampSelection = (timeStamp: string, currentMeridiem?: string) => {
    onTimeChange?.(is24HoursFormat ? timeStamp : `${timeStamp} ${currentMeridiem || meridiem}`);
  };
  const createTimeList = () => {
    const mappedTimeArray = timeList.map((time) => {
      const currentHour = is24HoursFormat
        ? getHoursIn24HourFormat(new Date().getHours().toString())
        : getHoursIn12HourFormat(new Date().getHours().toString());
      const splittedTime = time.name.split(':');
      const isDisabled = isDisabledTimeStamp(time.name, splittedTime[0] as string, currentHour);
      const isSelected = isValidSelection(splittedTime);

      const timeId = `time-${time.id}`;
      const timeCellCls = `${styles['time-cell']} ${isSelected && styles['selected-cell']} ${
        isDisabled && styles['disabled-cell']
      }`;

      const handleTimeCellEvent = () => handleTimeStampSelection(time.name);
      return (
        <span
          role="button"
          className={timeCellCls}
          key={time.id}
          onClick={handleTimeCellEvent}
          onKeyDown={handleTimeCellEvent}
          tabIndex={0}
          data-testid={time.name}
          aria-labelledby={timeId}
        >
          <span id={timeId}>{time.name}</span>
        </span>
      );
    });
    return splitArrayInGroups(mappedTimeArray, MAX_COLUMNS);
  };
  const handleInputBlur = () => {
    const currentHour = hours?.padStart(2, '0'),
      currentMin = minutes?.padStart(2, '0');
    handleTimeStampSelection(`${currentHour}:${currentMin}`);
  };
  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>, isHour = true) => {
    let { value } = e.target;
    const maxTime = is24HoursFormat ? MAX_HOURS_IN_24_HOUR_FORMAT : MAX_HOURS_IN_12_HOUR_FORMAT;
    if (isHour && Number(value.trim()) <= maxTime && Number(value.trim()) >= 0) {
      if (value === '00') {
        value = maxTime.toString();
      }
      setHours(value);
    }
    if (!isHour && Number(value.trim()) <= MAX_MINS && Number(value.trim()) >= 0) {
      if (value === MAX_MINS.toString()) {
        value = '00';
      }
      setMinutes(value);
    }
  };
  const onMeridiemChange = (value: string) => {
    setMeridiem(value);
    handleTimeStampSelection(`${hours}:${minutes}`, value);
  };

  return (
    <div className={styles['time-picker-container']} data-testid="time-picker-container">
      <div className={styles['time-picker-header']}>
        <span className={styles['time-picker-title']} data-testid="calendar-title-testid">
          {title}
        </span>
      </div>
      <div className={styles['time-input-container']}>
        {/* Hour input */}
        <div>
          <input
            className={styles['time-input']}
            data-testid="hour-time-input-test"
            type="text"
            inputMode="numeric"
            maxLength={MAX_TIME_INPUT_LENGTH}
            value={hours}
            onChange={handleTimeInputChange}
            onKeyDown={(e) => e.key === EVENT_KEY.ENTER && setHours(hours?.padStart(MAX_TIME_INPUT_LENGTH, '0'))}
            onBlur={handleInputBlur}
          />
        </div>
        {/* : */}
        <div className={styles['time-icon-seperator']}>
          <FAIcon
            iconName="colon"
            iconStyle={IconStyle.SOLID}
            theme={Theme.PURPLE}
            className={styles['colon-icon']}
            data-testid="colon-icon-test"
          />
        </div>
        {/* Minute input */}
        <div>
          <input
            className={styles['time-input']}
            data-testid="minute-time-input-test"
            type="text"
            inputMode="numeric"
            maxLength={MAX_TIME_INPUT_LENGTH}
            value={minutes}
            onChange={(e) => handleTimeInputChange(e, false)}
            onKeyDown={(e) => e.key === EVENT_KEY.ENTER && setMinutes(minutes?.padStart(MAX_TIME_INPUT_LENGTH, '0'))}
            onBlur={handleInputBlur}
          />
        </div>
        {/* Meridiem input */}
        {!is24HoursFormat && (
          <div className={styles['time-format-container']} data-testid="time-format-test">
            <button
              className={`${styles['time-format']} ${meridiem === TimeMeridiem.AM && styles['selected-time-format']}`}
              onClick={() => onMeridiemChange(TimeMeridiem.AM)}
              data-testid="time-format-AM-test"
            >
              {TimeMeridiem.AM}
            </button>
            <button
              className={`${styles['time-format']} ${meridiem === TimeMeridiem.PM && styles['selected-time-format']}`}
              onClick={() => onMeridiemChange(TimeMeridiem.PM)}
              data-testid="time-format-PM-test"
            >
              {TimeMeridiem.PM}
            </button>
          </div>
        )}
      </div>
      <div className={styles['time-container']}>
        {createTimeList().map((timeRow) => (
          <div className={styles['time-row']} key={`timerow-${timeRow.id}`}>
            {timeRow.name}
          </div>
        ))}
      </div>
    </div>
  );
};

TimePicker.displayName = 'TimePicker';
export default TimePicker;
